/**
 * EQUIPMENT BLOCK-COUNT SIZING — "how many of the selected block do I need?"
 *
 * Unlike `sizeUniformGenerationBlocks` (which inverts the solver for a free
 * per-block MW rating), the block rating here is FIXED by the applied
 * equipment envelope, so the only free variable is the integer block count.
 * Every candidate is an exact solver screen of a real re-application of the
 * envelope — no bisection, no rounding — and the screens honor the scenario's
 * own contingency criterion (N-1, N-2, …), so this path never pauses on
 * non-N-1 criteria.
 */
import type {
  EngineeringVerdict,
  GenerationImpactResult,
  GenerationImpactScenarioInput,
} from '../../types/generationImpact';
import {
  activeGenerationEquipmentSelection,
  resizeGenerationEquipmentFleet,
  type ActiveGenerationEquipmentSelection,
} from './equipmentSelectionState';
import { solveGenerationImpact } from './solveGenerationImpact';
import {
  GRID_SUPPLY_GENERATION_SIZING_NOT_APPLICABLE,
  isGridImportOnlyScenario,
} from './generationSizingValidation';

export interface EquipmentBlockCountCandidate {
  blockCount: number;
  contingencyVerdict: EngineeringVerdict;
  contingencyReserveVerdict: EngineeringVerdict;
  contingencyNetMW: number;
  /** The exact solver screen for this candidate fleet. */
  result: GenerationImpactResult;
  /** The resized scenario input the screen was solved from (apply-ready). */
  input: GenerationImpactScenarioInput;
}

export type EquipmentBlockCountSizingResult =
  | {
      status: 'available';
      selection: ActiveGenerationEquipmentSelection;
      candidates: readonly EquipmentBlockCountCandidate[];
      /** Smallest screened count clearing the contingency duty, if any. */
      minimumBlocksForDuty: number | null;
      /** Smallest screened count clearing duty + reserve, if any. */
      minimumBlocksForReserve: number | null;
    }
  | { status: 'unavailable'; reason: string };

export interface EquipmentBlockCountSizingOptions {
  /** Inclusive candidate range; defaults 2..16. */
  minBlockCount?: number;
  maxBlockCount?: number;
}

export function sizeGenerationEquipmentBlockCount(
  input: GenerationImpactScenarioInput,
  options: EquipmentBlockCountSizingOptions = {},
): EquipmentBlockCountSizingResult {
  if (isGridImportOnlyScenario(input)) {
    return { status: 'unavailable', reason: GRID_SUPPLY_GENERATION_SIZING_NOT_APPLICABLE };
  }
  const selection = activeGenerationEquipmentSelection(input);
  if (!selection) {
    return {
      status: 'unavailable',
      reason: 'No applied equipment selection — generic fleets size through the uniform planning line.',
    };
  }
  const minBlockCount = options.minBlockCount ?? 2;
  const maxBlockCount = options.maxBlockCount ?? 16;
  if (!Number.isInteger(minBlockCount) || !Number.isInteger(maxBlockCount)
    || minBlockCount < 1 || maxBlockCount > 64 || minBlockCount > maxBlockCount) {
    return {
      status: 'unavailable',
      reason: 'Block-count range must be integers with 1 ≤ min ≤ max ≤ 64.',
    };
  }

  const candidates: EquipmentBlockCountCandidate[] = [];
  for (let blockCount = minBlockCount; blockCount <= maxBlockCount; blockCount += 1) {
    const candidateInput = resizeGenerationEquipmentFleet(input, blockCount);
    const result = solveGenerationImpact(candidateInput);
    const capacity = result.capacity;
    candidates.push({
      blockCount,
      contingencyVerdict: capacity.contingencyVerdict ?? capacity.nMinusOneVerdict,
      contingencyReserveVerdict: capacity.contingencyReserveVerdict ?? capacity.nMinusOneReserveVerdict,
      contingencyNetMW: capacity.contingencyNetMW ?? capacity.nMinusOneNetMW,
      result,
      input: candidateInput,
    });
  }

  const minimumBlocksForDuty = candidates
    .find(candidate => candidate.contingencyVerdict === 'pass')?.blockCount ?? null;
  const minimumBlocksForReserve = candidates
    .find(candidate => candidate.contingencyVerdict === 'pass'
      && candidate.contingencyReserveVerdict === 'pass')?.blockCount ?? null;

  return {
    status: 'available',
    selection,
    candidates,
    minimumBlocksForDuty,
    minimumBlocksForReserve,
  };
}
